import axios from '../lib/axios';
import { AxiosError } from 'axios';
import { tokenStorage } from '../utils/tokenStorage';
import { guestSessionManager } from '../utils/guestSession';
import i18n from 'i18next';
import { logger } from '../utils/logger';
import { membershipService, type ProfileAPIResponse } from './membershipService';

// Cache/Single-flight for Share Types list and AI Limit Status
const SHARE_TYPES_CACHE_TTL = 30_000; // 30s
const SHARE_TYPES_FAILURE_COOLDOWN_MS = 10_000;
const AI_LIMIT_CACHE_TTL = 10_000; // 10s
const AI_LIMIT_FAILURE_COOLDOWN_MS = 10_000;

const userShareTypesCache = new Map<
  string,
  { response: GetUserShareTypesResponse; fetchedAt: number }
>();
const userShareTypesInFlight = new Map<string, Promise<GetUserShareTypesResponse>>();
let lastUserShareTypesErrorTime = 0;

const aiLimitCache = new Map<string, { response: AILimitStatusResponse; fetchedAt: number }>();
const aiLimitInFlight = new Map<string, Promise<AILimitStatusResponse>>();
let lastAiLimitErrorTime = 0;

// Single-flight + cache helpers for frequently called GET endpoints
const PAYMENT_METHODS_CACHE_TTL = 30_000; // 30s
const PAYMENT_METHODS_FAILURE_COOLDOWN_MS = 10_000;
const ORDER_CACHE_TTL = 15_000; // 15s
const ORDER_FAILURE_COOLDOWN_MS = 10_000;

// Payment methods types
export interface PaymentMethodItem {
  method: string;
  isEnabled: boolean;
  name: string;
  description?: string;
  pointsAvailable?: number;
  pointsRequired?: number;
}

export interface PaymentMethodsListData {
  paymentMethods: PaymentMethodItem[];
  userPoints?: number;
  pointsEligible?: boolean;
}

export interface PaymentMethodsListResponse {
  success: boolean;
  message: string;
  data?: PaymentMethodsListData;
}

// Payment methods (checkout session)
const paymentMethodsSessionCache = new Map<
  string,
  { response: PaymentMethodsListResponse; fetchedAt: number }
>();
const paymentMethodsSessionInFlight = new Map<string, Promise<PaymentMethodsListResponse>>();
let lastPaymentMethodsSessionErrorTime = 0;

// Payment methods (direct checkout)
const paymentMethodsDirectCache = new Map<
  string,
  { response: PaymentMethodsListResponse; fetchedAt: number }
>();
const paymentMethodsDirectInFlight = new Map<string, Promise<PaymentMethodsListResponse>>();
let lastPaymentMethodsDirectErrorTime = 0;

// Order by ID
export interface OrderByIdResponse {
  success: boolean;
  message: string;
  data?: unknown;
}
const orderByIdCache = new Map<string, { response: OrderByIdResponse; fetchedAt: number }>();
const orderByIdInFlight = new Map<string, Promise<OrderByIdResponse>>();
let lastOrderByIdErrorTime = 0;

// API Endpoints from environment variables
const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_ENDPOINTS = {
  ORDERS: import.meta.env.VITE_API_V1_ORDERS,
  ORDERS_PAYMENT_STATUS: import.meta.env.VITE_API_V1_ORDERS_PAYMENT_STATUS,
  CHECKOUT_INITIALIZE: import.meta.env.VITE_API_V1_CHECKOUT_INITIALIZE,
  CHECKOUT_PAYMENT_METHOD: import.meta.env.VITE_API_V1_CHECKOUT_PAYMENT_METHOD,
  CHECKOUT_COMPLETE: import.meta.env.VITE_API_V1_CHECKOUT_COMPLETE,
  CHECKOUT_DIRECT: import.meta.env.VITE_API_V1_CHECKOUT_DIRECT,
  CART_REMOVE_UNAVAILABLE: import.meta.env.VITE_API_V1_CART_REMOVE_UNAVAILABLE,
  // PAYMENTS: import.meta.env.VITE_API_PAYMENTS,
  USERS_PROFILE: import.meta.env.VITE_API_V1_USERS_PROFILE,
  CARDS_SCRATCH: import.meta.env.VITE_API_V1_CARDS_SCRATCH,
};

export interface OrderItem {
  id: string;
  productId: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
}

export interface CartSummary {
  items: OrderItem[];
  discount?: {
    code: string;
    type: 'PERCENTAGE' | 'FIXED';
    value: number;
    discountAmount: number;
  };
  subtotal: number;
  totalPrice: number;
}

export interface CheckoutResponse {
  success: boolean;
  message: string;
  data: {
    checkoutId: string;
    cartSummary: CartSummary;
  };
}

export interface CreateOrderRequest {
  paymentMethod: string;
  notes?: string;
}

export interface CreateOrderResponse {
  success: boolean;
  message: string;
  data: {
    orderId: string;
    status: string;
    paymentStatus: string;
    totalAmount: number;
    items: OrderItem[];
  };
}

export interface UpdatePaymentRequest {
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded' | 'cancelled';
}

export interface UpdateOrderStatusRequest {
  orderStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
}

// Helper function to get headers for both authenticated and guest users
const getHeaders = async () => {
  const token = tokenStorage.getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': i18n.language || 'en',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    // For guest users, get or create guest session and set as cookie
    const guestId = await guestSessionManager.getSession();
    headers.Cookie = `gid=${guestId}`;
  }

  return headers;
};

// Helper to check if offline mode is enabled
// Helper to check if user is in merchant mode
const isMerchantMode = () => {
  try {
    const profileStr = localStorage.getItem('offline_user_profile');
    if (profileStr) {
      const profile = JSON.parse(profileStr);
      return profile.activeRole === 'merchant';
    }
  } catch (e) {
    logger.warn('Failed to parse offline profile for role check', e);
  }
  return false;
};

// Helper to check if offline mode is enabled
const isOfflineModeEnabled = () => {
  return localStorage.getItem('offline_mode_enabled') === 'true' && isMerchantMode();
};

// Create order (checkout)
export const createOrder = async (data: CreateOrderRequest): Promise<CreateOrderResponse> => {
  const headers = await getHeaders();
  const response = await axios.post('/api/v1/orders/checkout', data, {
    headers,
  });
  return response.data;
};

// Update payment status
export const updatePaymentStatus = async (
  orderId: string,
  data: UpdatePaymentRequest,
): Promise<{ success: boolean; message: string; data?: unknown }> => {
  const headers = await getHeaders();
  const response = await axios.put(`/api/v1/orders/${orderId}/payment`, data, {
    headers,
  });
  return response.data;
};

// Update order status
export const updateOrderStatus = async (
  orderId: string,
  data: UpdateOrderStatusRequest,
): Promise<{ success: boolean; message: string; data?: unknown }> => {
  const headers = await getHeaders();
  const response = await axios.put(`/api/v1/orders/${orderId}/status`, data, {
    headers,
  });
  return response.data;
};

// Get order by ID
export const getOrderById = async (
  orderId: string,
): Promise<{ success: boolean; message: string; data?: unknown }> => {
  const headers = await getHeaders();

  // Check offline first
  if (!navigator.onLine && isOfflineModeEnabled()) {
    // Check offline_orders_list
    const offlineOrders = localStorage.getItem('offline_orders_list');
    if (offlineOrders) {
      try {
        const allOrders = JSON.parse(offlineOrders);
        if (Array.isArray(allOrders)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const order = allOrders.find((o: any) => o._id === orderId || o.id === orderId);
          if (order) {
            // Ensure cards array exists to prevent UI crashes
            if (!order.cards) {
              order.cards = [];
            }
            logger.log(
              'Returning offline order from list cache for:',
              orderId,
              'Cards:',
              order.cards?.length,
            );
            return { success: true, message: 'Offline data from list', data: order };
          }
        }
      } catch (e) {
        logger.error('Error parsing offline orders list:', e);
      }
    }
    // If offline and no cached data, throw error
    throw new Error('No offline data available');
  }

  // Network request
  if (lastOrderByIdErrorTime && Date.now() - lastOrderByIdErrorTime < ORDER_FAILURE_COOLDOWN_MS) {
    const err = new Error('Order fetch paused due to recent errors');
    (err as Error & { isCooldown?: boolean }).isCooldown = true;
    throw err;
  }

  const existingPromise = orderByIdInFlight.get(orderId);
  if (existingPromise) {
    return await existingPromise;
  }

  const requestPromise = axios
    .get(`/api/v1/orders/${orderId}`, { headers })
    .then((response) => {
      orderByIdCache.set(orderId, { response: response.data, fetchedAt: Date.now() });

      // Save to offline storage if enabled
      if (isOfflineModeEnabled() && response.data.success) {
        try {
          const orderData = response.data.data;
          // Ensure cards array exists before saving
          if (!orderData.cards) {
            orderData.cards = [];
          }

          // Update offline_orders_list
          const offlineOrdersStr = localStorage.getItem('offline_orders_list');
          let offlineOrders: Record<string, unknown>[] = [];

          if (offlineOrdersStr) {
            try {
              const parsed = JSON.parse(offlineOrdersStr);
              if (Array.isArray(parsed)) {
                offlineOrders = parsed;
              }
            } catch (e) {
              logger.error('Error parsing offline orders list for update:', e);
            }
          }

          const orderIndex = offlineOrders.findIndex(
            (o) =>
              (o as { _id?: string; id?: string })._id === orderId ||
              (o as { _id?: string; id?: string }).id === orderId,
          );
          if (orderIndex !== -1) {
            // Merge the full details (including cards) into the existing list item
            offlineOrders[orderIndex] = { ...offlineOrders[orderIndex], ...orderData };
            logger.log('Updated offline order list cache for:', orderId);
          } else {
            // New order, append to the list
            // Add to the beginning of the list as it's likely a new/recent order
            offlineOrders.unshift(orderData);
            logger.log('Added new order to offline list cache:', orderId);
          }

          localStorage.setItem('offline_orders_list', JSON.stringify(offlineOrders));
        } catch (e) {
          logger.error('Failed to save order details offline:', e);
        }
      }

      return response.data;
    })
    .catch((error) => {
      lastOrderByIdErrorTime = Date.now();
      throw error;
    })
    .finally(() => {
      orderByIdInFlight.delete(orderId);
    });

  orderByIdInFlight.set(orderId, requestPromise);
  return await requestPromise;
};

// Get all orders
export const getAllOrders = async (
  page: number = 1,
  limit: number = 10,
  status?: string,
): Promise<{
  success: boolean;
  message: string;
  data?: unknown;
  meta?: { total: number; pages: number; page: number; limit: number };
}> => {
  // Check offline first
  if (!navigator.onLine && isOfflineModeEnabled()) {
    const offlineOrders = localStorage.getItem('offline_orders_list');
    if (offlineOrders) {
      try {
        const allOrders = JSON.parse(offlineOrders);
        // Basic client-side pagination/filtering could be implemented here if needed
        // For now, just returning all cached orders if page 1, or empty if not
        logger.log('Returning offline orders list');
        return {
          success: true,
          message: 'Offline data',
          data: allOrders,
          meta: { total: allOrders.length, pages: 1, page: 1, limit: allOrders.length },
        };
      } catch (e) {
        logger.error('Error parsing offline orders list:', e);
      }
    }
  }

  const headers = await getHeaders();
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    expand: 'true',
    ...(status && { status }),
  });
  const url = `${BASE_URL}${API_ENDPOINTS.ORDERS}?${queryParams.toString()}`;
  const cacheKey = queryParams.toString();

  // Reuse ORDER cache settings
  const cached = orderByIdCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < ORDER_CACHE_TTL) {
    return cached.response;
  }

  if (lastOrderByIdErrorTime && Date.now() - lastOrderByIdErrorTime < ORDER_FAILURE_COOLDOWN_MS) {
    const err = new Error('Orders fetch paused due to recent errors');
    (err as Error & { isCooldown?: boolean }).isCooldown = true;
    throw err;
  }

  const existingPromise = orderByIdInFlight.get(cacheKey);
  if (existingPromise) {
    return await existingPromise;
  }

  const requestPromise = axios
    .get(url, { headers })
    .then((response) => {
      orderByIdCache.set(cacheKey, { response: response.data, fetchedAt: Date.now() });

      // Save to offline storage if enabled
      if (isOfflineModeEnabled() && response.data.success && page === 1 && !status) {
        // Trigger background fetch of ALL orders to ensure complete offline history
        // We don't await this to avoid blocking the UI
        fetchAllOrdersInBackground(response.data.data, response.data.meta).catch((err) =>
          logger.error('Background order fetch failed:', err),
        );
      }

      return response.data;
    })
    .catch((error) => {
      lastOrderByIdErrorTime = Date.now();
      throw error;
    })
    .finally(() => {
      orderByIdInFlight.delete(cacheKey);
    });

  orderByIdInFlight.set(cacheKey, requestPromise);
  return await requestPromise;
};

// Helper to fetch all orders in background for offline cache
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchAllOrdersInBackground = async (initialOrders: any[], initialMeta: any) => {
  try {
    logger.log('Starting background fetch of all orders for offline cache...');
    const headers = await getHeaders();
    let allOrders = [...initialOrders];
    let page = 2;
    const totalPages = initialMeta?.pages || 1;

    // If we already have all orders (1 page), just save and return
    if (totalPages <= 1) {
      localStorage.setItem('offline_orders_list', JSON.stringify(allOrders));
      logger.log(`Cached ${allOrders.length} orders for offline mode (single page)`);
      return;
    }

    // Fetch remaining pages
    while (page <= totalPages) {
      // Add a small delay to avoid overwhelming the server
      await new Promise((resolve) => setTimeout(resolve, 500));

      const response = await axios.get(`${BASE_URL}${API_ENDPOINTS.ORDERS}`, {
        headers,
        params: { page, limit: 10, expand: 'true' }, // Keep limit consistent with initial fetch
      });

      if (response.data.success) {
        const orders = response.data.data;
        if (Array.isArray(orders)) {
          allOrders = [...allOrders, ...orders];
        }
      }
      page++;
    }

    if (allOrders.length > 0) {
      localStorage.setItem('offline_orders_list', JSON.stringify(allOrders));
      logger.log(`Cached ${allOrders.length} orders for offline mode`);
    }
  } catch (error) {
    logger.error('Failed to fetch all orders in background:', error);
  }
};

// Explicit sync function for offline mode with progress reporting
export const syncOfflineData = async (onProgress?: (progress: number) => void) => {
  try {
    logger.log('Starting explicit offline data sync...');
    const headers = await getHeaders();

    // 1. Fetch first page to get metadata
    if (onProgress) onProgress(10);
    const initialResponse = await axios.get(`${BASE_URL}${API_ENDPOINTS.ORDERS}`, {
      headers,
      params: { page: 1, limit: 10, expand: 'true' },
    });

    if (!initialResponse.data.success) {
      throw new Error('Failed to fetch initial orders');
    }

    let allOrders = [...initialResponse.data.data];
    const totalPages = initialResponse.data.meta?.pages || 1;

    if (onProgress) onProgress(20);

    // 2. Fetch remaining pages
    if (totalPages > 1) {
      let page = 2;
      while (page <= totalPages) {
        // Calculate progress based on pages fetched (20% to 90%)
        const progress = 20 + Math.floor(((page - 1) / totalPages) * 70);
        if (onProgress) onProgress(progress);

        // Add a small delay to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 300));

        const response = await axios.get(`${BASE_URL}${API_ENDPOINTS.ORDERS}`, {
          headers,
          params: { page, limit: 10, expand: 'true' },
        });

        if (response.data.success && Array.isArray(response.data.data)) {
          allOrders = [...allOrders, ...response.data.data];
        }
        page++;
      }
    }

    // 4. Save to local storage
    if (onProgress) onProgress(95);
    localStorage.setItem('offline_orders_list', JSON.stringify(allOrders));

    // Also fetch user profile for offline use
    try {
      await axios.get(API_ENDPOINTS.USERS_PROFILE, { headers }).then((res) => {
        if (res.data.user) {
          localStorage.setItem('offline_user_profile', JSON.stringify(res.data.user));
        }
      });
    } catch (e) {
      logger.warn('Failed to cache profile during sync:', e);
    }

    if (onProgress) onProgress(100);
    logger.log(`Synced ${allOrders.length} orders with details for offline mode`);
    return { success: true, count: allOrders.length };
  } catch (error) {
    logger.error('Offline sync failed:', error);
    throw error;
  }
};

// Get shared order
export const getSharedOrder = async (shareId: string): Promise<SharedOrderResponse> => {
  try {
    const headers = await getHeaders();
    const cacheKey = `shared-${shareId}`;

    const cached = orderByIdCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < ORDER_CACHE_TTL) {
      return cached.response as SharedOrderResponse;
    }

    if (lastOrderByIdErrorTime && Date.now() - lastOrderByIdErrorTime < ORDER_FAILURE_COOLDOWN_MS) {
      const err = new Error('Shared order fetch paused due to recent errors');
      (err as Error & { isCooldown?: boolean }).isCooldown = true;
      throw err;
    }

    const existingPromise = orderByIdInFlight.get(cacheKey);
    if (existingPromise) {
      return (await existingPromise) as SharedOrderResponse;
    }

    const requestPromise = axios
      .get(`${BASE_URL}/api/v1/orders/shared/${shareId}`, { headers })
      .then((response) => {
        orderByIdCache.set(cacheKey, { response: response.data, fetchedAt: Date.now() });
        return response.data as SharedOrderResponse;
      })
      .catch((error) => {
        lastOrderByIdErrorTime = Date.now();
        throw error;
      })
      .finally(() => {
        orderByIdInFlight.delete(cacheKey);
      });

    orderByIdInFlight.set(cacheKey, requestPromise);
    return (await requestPromise) as SharedOrderResponse;
  } catch (error: unknown) {
    logger.error('Get shared order error:', error);
    const errorMessage =
      error instanceof AxiosError && error.response?.data?.message
        ? error.response.data.message
        : i18n.t('errors.getSharedOrderFailed') || 'Failed to get shared order';
    throw new Error(errorMessage);
  }
};

// Apply promo code
export const applyPromoCode = async (
  checkoutId: string,
  promoCode: string,
): Promise<CheckoutResponse> => {
  const headers = await getHeaders();
  const response = await axios.post(
    '/api/promo',
    {
      checkoutId,
      promo: promoCode,
    },
    {
      headers,
    },
  );
  return response.data;
};

// Remove promo code
export const removePromoCode = async (
  checkoutId: string,
): Promise<{ success: boolean; message: string; data?: unknown }> => {
  const headers = await getHeaders();
  const response = await axios.delete('/api/promo', {
    data: { checkoutId },
    headers,
  });
  return response.data;
};

// Get payment methods by session ID (for checkout flow)
export const getPaymentMethodsBySession = async (
  sessionId: string,
): Promise<{
  success: boolean;
  message: string;
  data?: {
    paymentMethods: Array<{
      method: string;
      isEnabled: boolean;
      name: string;
      description?: string;
      pointsAvailable?: number;
      pointsRequired?: number;
    }>;
    userPoints?: number;
    pointsEligible?: boolean;
  };
}> => {
  const headers = await getHeaders();
  const url = `${BASE_URL}/api/v1/checkout/payment-methods?sessionId=${sessionId}`;

  // Single-flight + cache per sessionId
  const cached = paymentMethodsSessionCache.get(sessionId);
  if (cached && Date.now() - cached.fetchedAt < PAYMENT_METHODS_CACHE_TTL) {
    return cached.response;
  }

  if (
    lastPaymentMethodsSessionErrorTime &&
    Date.now() - lastPaymentMethodsSessionErrorTime < PAYMENT_METHODS_FAILURE_COOLDOWN_MS
  ) {
    const err = new Error('Payment methods fetch paused due to recent errors');
    (err as Error & { isCooldown?: boolean }).isCooldown = true;
    throw err;
  }

  const existingPromise = paymentMethodsSessionInFlight.get(sessionId);
  if (existingPromise) {
    return await existingPromise;
  }

  const requestPromise = axios
    .get(url, { headers })
    .then((response) => {
      paymentMethodsSessionCache.set(sessionId, { response: response.data, fetchedAt: Date.now() });
      return response.data;
    })
    .catch((error) => {
      lastPaymentMethodsSessionErrorTime = Date.now();
      throw error;
    })
    .finally(() => {
      paymentMethodsSessionInFlight.delete(sessionId);
    });

  paymentMethodsSessionInFlight.set(sessionId, requestPromise);
  return await requestPromise;
};

// Get payment methods for direct checkout (using productId and userId)
export const getPaymentMethodsDirectCheckout = async (
  checkoutAmount: number,
): Promise<{
  success: boolean;
  message: string;
  data?: {
    paymentMethods: Array<{
      method: string;
      isEnabled: boolean;
      name: string;
      description?: string;
      pointsAvailable?: number;
      pointsRequired?: number;
    }>;
    userPoints?: number;
    pointsEligible?: boolean;
  };
}> => {
  const headers = await getHeaders();
  const url = `${BASE_URL}/api/v1/payment-methods?checkoutAmount=${checkoutAmount}`;

  const key = `${checkoutAmount}`;

  // Single-flight + cache per amount
  const cached = paymentMethodsDirectCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < PAYMENT_METHODS_CACHE_TTL) {
    return cached.response;
  }

  if (
    lastPaymentMethodsDirectErrorTime &&
    Date.now() - lastPaymentMethodsDirectErrorTime < PAYMENT_METHODS_FAILURE_COOLDOWN_MS
  ) {
    const err = new Error('Payment methods (direct) fetch paused due to recent errors');
    (err as Error & { isCooldown?: boolean }).isCooldown = true;
    throw err;
  }

  const existingPromise = paymentMethodsDirectInFlight.get(key);
  if (existingPromise) {
    return await existingPromise;
  }

  const requestPromise = axios
    .get(url, { headers })
    .then((response) => {
      paymentMethodsDirectCache.set(key, { response: response.data, fetchedAt: Date.now() });
      return response.data;
    })
    .catch((error) => {
      lastPaymentMethodsDirectErrorTime = Date.now();
      throw error;
    })
    .finally(() => {
      paymentMethodsDirectInFlight.delete(key);
    });

  paymentMethodsDirectInFlight.set(key, requestPromise);
  return await requestPromise;
};

// Legacy function for backward compatibility (deprecated)
export const getPaymentMethods = async (params?: {
  sessionId?: string;
}): Promise<{
  success: boolean;
  message: string;
  data?: {
    paymentMethods: Array<{
      method: string;
      isEnabled: boolean;
      name: string;
      description?: string;
      pointsAvailable?: number;
      pointsRequired?: number;
    }>;
    userPoints?: number;
    pointsEligible?: boolean;
  };
}> => {
  const headers = await getHeaders();
  let url = `${BASE_URL}/api/v1/checkout/payment-methods`;

  if (params) {
    const queryParams = new URLSearchParams();
    if (params.sessionId) {
      queryParams.append('sessionId', params.sessionId);
    }

    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }
  }

  const response = await axios.get(url, {
    headers,
  });
  return response.data;
};

// Get user information
export const getUserInfo = async (): Promise<ProfileAPIResponse> => {
  try {
    // Reuse membershipService centralized profile fetch to avoid duplicate calls
    const response = await membershipService.getProfile();
    return response.data;
  } catch (error) {
    // Fallback to original request if centralized fetch throws cooldown error or other issues
    logger.warn('Profile fetch failed in getUserInfo, falling back to direct request:', error);
    const headers = await getHeaders();
    const response = await axios.get(API_ENDPOINTS.USERS_PROFILE, { headers });
    return response.data;
  }
};

// New checkout API functions
export interface InitializeCheckoutResponse {
  success: boolean;
  message?: string;
  data?: {
    sessionId: string;
    status: string;
    items: Array<{
      productId: string;
      quantity: number;
      price: number;
      currentPrice: number;
      name: {
        en: string;
        my: string;
      };
      image: string;
      code: string;
      subtotal: number;
      priceChanged: boolean;
      isAvailable: boolean;
    }>;
    pricing: {
      subtotal: number;
      tax: number;
      taxRate: number;
      shipping: number;
      discountAmount: number;
      total: number;
    };
    expiresAt: string;
  };
  unavailableItems?: Array<{
    productId: string;
    name: {
      en: string;
      my: string;
    };
    reason: string;
    image: string;
  }>;
}

export interface UnavailableItem {
  productId: string;
  name: {
    en: string;
    my: string;
  };
  reason: string;
  image: string;
}

export interface RemoveUnavailableItemsRequest {
  unavailableItems: UnavailableItem[];
}

export interface RemoveUnavailableItemsResponse {
  success: boolean;
  message: string;
}

export interface PaymentMethodRequest {
  paymentMethod: string;
  sessionId: string;
}

export interface PaymentMethodResponse {
  success: boolean;
  data: {
    sessionId: string;
    paymentMethod: {
      method: string;
      isValid: boolean;
      validatedAt: string;
      amount: number;
      currency: string;
      fees: {
        fixedFee: number;
        percentageFee: number;
        totalFee: number;
      };
    };
  };
}

export interface CompleteCheckoutResponse {
  success: boolean;
  data: {
    orderId: string;
    orderNumber: string;
    paymentQrCode: string;
    pushNotiLink?: string;
    deepLink?: string;
  };
}

// Initialize checkout
export const initializeCheckout = async (
  cartItems?: Array<{
    productId: string;
    quantity: number;
    name?: { en: string; my: string };
    image?: string;
  }>,
): Promise<InitializeCheckoutResponse> => {
  const headers = await getHeaders();

  // Prepare request body with cart items if provided
  // const requestBody = cartItems && cartItems.length > 0 ? {
  //   lineItems: cartItems
  // } : {};

  // Try with empty body first to test API
  const requestBody = {};

  logger.log('Initialize checkout request body:', requestBody);

  // Mock response to test unavailable items dialog since API is returning 400
  if (cartItems && cartItems.length > 0) {
    logger.log('Returning mock response with unavailable items');

    // Use actual cart item data when available
    const unavailableItems = cartItems.map((item) => ({
      productId: item.productId,
      name: item.name || {
        en: `Product ${item.productId.slice(-4)}`, // Fallback to last 4 chars of productId
        my: `ထုတ်ကုန် ${item.productId.slice(-4)}`,
      },
      reason: 'Out of stock',
      image: item.image || '/placeholder-image.jpg',
    }));

    return {
      success: false,
      message: 'Some items in your cart are no longer available.',
      unavailableItems,
    };
  }

  const response = await axios.post(
    `${BASE_URL}${API_ENDPOINTS.CHECKOUT_INITIALIZE}`,
    requestBody,
    {
      headers,
    },
  );
  return response.data;
};

// Select payment method
export const selectPaymentMethod = async (
  data: PaymentMethodRequest,
): Promise<PaymentMethodResponse> => {
  const headers = await getHeaders();
  const response = await axios.post(`${BASE_URL}${API_ENDPOINTS.CHECKOUT_PAYMENT_METHOD}`, data, {
    headers,
  });
  return response.data;
};

// Complete checkout
export const completeCheckout = async (sessionId: string): Promise<CompleteCheckoutResponse> => {
  const headers = await getHeaders();
  const response = await axios.post(
    `${BASE_URL}${API_ENDPOINTS.CHECKOUT_COMPLETE}`,
    {
      sessionId,
    },
    {
      headers,
    },
  );
  return response.data;
};

// Remove unavailable items from cart
export const removeUnavailableItems = async (
  data: RemoveUnavailableItemsRequest,
): Promise<RemoveUnavailableItemsResponse> => {
  const headers = await getHeaders();
  const response = await axios.post(`${BASE_URL}${API_ENDPOINTS.CART_REMOVE_UNAVAILABLE}`, data, {
    headers,
  });
  return response.data;
};

export const scratchCard = async (
  cardId: string,
): Promise<{ success: boolean; message: string; data?: unknown }> => {
  // Handle offline scratch
  if (!navigator.onLine && isOfflineModeEnabled()) {
    try {
      const offlineScratched = JSON.parse(localStorage.getItem('offline_scratched_cards') || '[]');
      if (!offlineScratched.includes(cardId)) {
        offlineScratched.push(cardId);
        localStorage.setItem('offline_scratched_cards', JSON.stringify(offlineScratched));
      }

      // Update local order details to reflect scratch status
      const offlineOrdersStr = localStorage.getItem('offline_orders_list');
      if (offlineOrdersStr) {
        try {
          const offlineOrders = JSON.parse(offlineOrdersStr);
          if (Array.isArray(offlineOrders)) {
            let updated = false;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            offlineOrders.forEach((order: any) => {
              if (order.cards) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const cardIndex = order.cards.findIndex((c: any) => c._id === cardId);
                if (cardIndex !== -1) {
                  order.cards[cardIndex].scratch = true;
                  updated = true;
                }
              }
            });

            if (updated) {
              localStorage.setItem('offline_orders_list', JSON.stringify(offlineOrders));
              logger.log('Updated offline order list with scratched card:', cardId);
            }
          }
        } catch (e) {
          logger.error('Error updating offline orders list for scratch:', e);
        }
      }

      return { success: true, message: 'Card scratched offline' };
    } catch (e) {
      logger.error('Failed to handle offline scratch:', e);
      throw new Error('Failed to scratch card offline');
    }
  }

  try {
    const headers = await getHeaders();
    const response = await axios.put(
      `${API_ENDPOINTS.CARDS_SCRATCH.replace('{cardId}', cardId)}`,
      {
        scratch: true,
      },
      { headers },
    );
    return response.data;
  } catch (error: unknown) {
    logger.error('Scratch card error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(errorMessage);
  }
};

// Direct checkout interfaces and function
export interface DirectCheckoutRequest {
  item: {
    productId: string;
    quantity: number;
    phoneNumber: string;
  };
  paymentMethod: string;
}

export interface DirectCheckoutResponse {
  success: boolean;
  message?: string;
  data?: {
    orderId: string;
    orderNumber: string;
    paymentQrCode: string;
    totalAmount: number;
    status: string;
    paymentMethod?: {
      method: string;
      isValid: boolean;
      validatedAt: string;
      amount: number;
      currency: string;
      fees: {
        fixedFee: number;
        percentageFee: number;
        totalFee: number;
      };
    };
  };
}

export const directCheckout = async (
  data: DirectCheckoutRequest,
): Promise<DirectCheckoutResponse> => {
  const headers = await getHeaders();
  const response = await axios.post(`${BASE_URL}${API_ENDPOINTS.CHECKOUT_DIRECT}`, data, {
    headers,
  });
  return response.data;
};

// Share Order Types
export interface ShareOrderRequest {
  type?: 'Gift' | 'Treat' | 'Share' | 'Allowance' | 'Repayment' | 'Love' | 'Receipt';
  note?: string;
  shareTypeId?: string;
}

export interface ShareOrderResponse {
  success: boolean;
  message: string;
  data: {
    shareId: string;
    shareUrl: string;
    type: string;
    expiresAt?: string;
  };
}

export interface SharedOrderResponse {
  success: boolean;
  message: string;
  data: {
    order: {
      id: string;
      orderNumber: string;
      status: string;
      totalAmount: number;
      items: OrderItem[];
      createdAt: string;
      sharedBy: {
        name?: string;
        email?: string;
      };
      shareType: string;
      shareMessage?: string;
    };
  };
}

// Generate share link for an order
export const generateShareLink = async (
  orderId: string,
  data: ShareOrderRequest,
): Promise<ShareOrderResponse> => {
  try {
    const headers = await getHeaders();
    const response = await axios.post(`${BASE_URL}${API_ENDPOINTS.ORDERS}/${orderId}/share`, data, {
      headers,
    });
    return response.data;
  } catch (error: unknown) {
    // Handle 208 status code for existing share info
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      (error as { response: { status: number } }).response?.status === 208
    ) {
      return {
        success: true,
        message:
          (error as { response: { data?: { message?: string } } }).response.data?.message ||
          'Share link already exists',
        data: (
          error as {
            response: {
              data?: {
                data?: { shareId: string; shareUrl: string; type: string; expiresAt?: string };
              };
            };
          }
        ).response.data?.data || {
          shareId: '',
          shareUrl: '',
          type: data.type || 'Share', // Provide a default type if data.type is undefined
          expiresAt: undefined,
        },
      };
    }
    const errorMessage =
      error &&
      typeof error === 'object' &&
      'response' in error &&
      (error as { response: { data?: { message?: string } } }).response.data?.message;
    throw new Error(
      typeof errorMessage === 'string' ? errorMessage : 'Failed to generate share link',
    );
  }
};

// Disable sharing for an order
export const disableOrderSharing = async (
  orderId: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const headers = await getHeaders();
    const response = await axios.delete(`${BASE_URL}${API_ENDPOINTS.ORDERS}/${orderId}/share`, {
      headers,
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage =
      error &&
      typeof error === 'object' &&
      'response' in error &&
      (error as { response: { data?: { message?: string } } }).response.data?.message;
    throw new Error(typeof errorMessage === 'string' ? errorMessage : 'Failed to disable sharing');
  }
};

export const cancelOrder = async (
  orderId: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const headers = await getHeaders();
    const response = await axios.put(
      `${BASE_URL}${API_ENDPOINTS.ORDERS}/${orderId}/cancel-payment`,
      {},
      { headers },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to cancel order:', error);
    throw error;
  }
};

export const regenerateQR = async (orderId: string): Promise<string> => {
  const token = tokenStorage.getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': 'en',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    // For guest users, get session and set as cookie
    const guestId = await guestSessionManager.getSession();
    headers.Cookie = `gid=${guestId}`;
  }

  const response = await axios.put(
    `${import.meta.env.VITE_API_BASE_URL}/api/v1/orders/${orderId}/regenerate-qr`,
    {},
    {
      headers,
      withCredentials: true,
    },
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to regenerate QR code');
  }

  return response.data.data.paymentQrCode;
};

// AI Share Type Generation
export interface AIGenerateShareTypeRequest {
  userText: string;
}

export interface AIGenerateShareTypeResponse {
  success: boolean;
  message: string;
  data: {
    [key: string]: {
      icon: string;
      bgGradient: string;
      cardGradient: string;
      accentColor: string;
      textColor: string;
      particles: string[];
    };
  };
}

export const generateAIShareType = async (
  data: AIGenerateShareTypeRequest,
): Promise<{
  success: boolean;
  message: string;
  data: {
    title: string;
    icon: string;
    bgGradient: string;
    cardGradient: string;
    accentColor: string;
    textColor: string;
    particles: string[];
  };
}> => {
  try {
    const headers = await getHeaders();
    const response = await axios.post(`${BASE_URL}/api/v1/sharetypes/generate-style`, data, {
      headers,
    });

    if (response.data.success) {
      // Extract the first key from data object and use its value
      const dataKey = Object.keys(response.data.data)[0];
      const shareTypeData = response.data.data[dataKey];

      // Return with the expected format including the title from the key
      return {
        success: response.data.success,
        message: response.data.message,
        data: {
          title: dataKey,
          ...shareTypeData,
        },
      };
    } else {
      throw new Error(response.data.message || 'Failed to generate AI share type');
    }
  } catch (error: unknown) {
    logger.error('Error generating AI share type:', error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to generate AI share type');
  }
};

// Add AI Generated Share Type
export interface AddAIShareTypeRequest {
  title: string;
  icon: string;
  bgGradient: string;
  cardGradient: string;
  accentColor: string;
  textColor: string;
  particles: string[];
}

export interface AddAIShareTypeResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    title: string;
    icon: string;
    bgGradient: string;
    cardGradient: string;
    accentColor: string;
    textColor: string;
    particles: string[];
  };
}

export const addAIShareType = async (
  data: AddAIShareTypeRequest,
): Promise<AddAIShareTypeResponse> => {
  try {
    const headers = await getHeaders();
    const response = await axios.post(`${BASE_URL}/api/v1/sharetypes`, data, {
      headers,
    });

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || 'Failed to add AI share type');
    }
  } catch (error: unknown) {
    logger.error('Error adding AI share type:', error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to add AI share type');
  }
};

// Get user created share types
export interface GetUserShareTypesRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface UserShareType {
  id?: string;
  _id: string;
  title: string;
  icon: string;
  bgGradient: string;
  cardGradient: string;
  accentColor: string;
  textColor: string;
  particles: string[];
  createdAt: string;
  updatedAt: string;
}

export interface GetUserShareTypesResponse {
  success: boolean;
  message: string;
  data: UserShareType[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const getUserShareTypes = async (
  params: GetUserShareTypesRequest = {},
): Promise<GetUserShareTypesResponse> => {
  try {
    const headers = await getHeaders();
    const { page = 1, limit = 10, search } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
    });
    const cacheKey = queryParams.toString();

    // Cache hit within TTL
    const cached = userShareTypesCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < SHARE_TYPES_CACHE_TTL) {
      return cached.response as GetUserShareTypesResponse;
    }

    // Failure cooldown
    if (
      lastUserShareTypesErrorTime &&
      Date.now() - lastUserShareTypesErrorTime < SHARE_TYPES_FAILURE_COOLDOWN_MS
    ) {
      const err = new Error('Share types fetch paused due to recent errors');
      (err as Error & { isCooldown?: boolean }).isCooldown = true;
      throw err;
    }

    // Single-flight
    const existingPromise = userShareTypesInFlight.get(cacheKey);
    if (existingPromise) {
      return (await existingPromise) as GetUserShareTypesResponse;
    }

    const requestPromise = axios
      .get(`${BASE_URL}/api/v1/sharetypes?${cacheKey}`, { headers })
      .then((response) => {
        userShareTypesCache.set(cacheKey, { response: response.data, fetchedAt: Date.now() });
        return response.data as GetUserShareTypesResponse;
      })
      .catch((error) => {
        lastUserShareTypesErrorTime = Date.now();
        throw error;
      })
      .finally(() => {
        userShareTypesInFlight.delete(cacheKey);
      });

    userShareTypesInFlight.set(cacheKey, requestPromise);
    return (await requestPromise) as GetUserShareTypesResponse;
  } catch (error: unknown) {
    logger.error('Get user share types error:', error);
    const errorMessage =
      error instanceof AxiosError && error.response?.data?.message
        ? error.response.data.message
        : i18n.t('errors.getUserShareTypesFailed') || 'Failed to get user share types';
    throw new Error(errorMessage);
  }
};

// Delete user share type
export const deleteUserShareType = async (
  shareTypeId: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const headers = await getHeaders();
    const response = await axios.delete(`${BASE_URL}/api/v1/sharetypes/${shareTypeId}`, {
      headers,
    });
    return response.data;
  } catch (error: unknown) {
    logger.error('Delete user share type error:', error);
    const errorMessage =
      error instanceof AxiosError && error.response?.data?.message
        ? error.response.data.message
        : i18n.t('errors.deleteShareTypeFailed') || 'Failed to delete share type';
    throw new Error(errorMessage);
  }
};

// AI Generation Limit Status
export interface AILimitStatusResponse {
  success: boolean;
  message: string;
  data: {
    dailyLimit: number;
    currentUsage: number;
    remainingCount: number;
    resetTime: string;
  };
}

export const getAILimitStatus = async (): Promise<AILimitStatusResponse> => {
  try {
    const headers = await getHeaders();
    const cacheKey = 'ai-limit-status';

    const cached = aiLimitCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < AI_LIMIT_CACHE_TTL) {
      return cached.response as AILimitStatusResponse;
    }

    if (lastAiLimitErrorTime && Date.now() - lastAiLimitErrorTime < AI_LIMIT_FAILURE_COOLDOWN_MS) {
      const err = new Error('AI limit status fetch paused due to recent errors');
      (err as Error & { isCooldown?: boolean }).isCooldown = true;
      throw err;
    }

    const existingPromise = aiLimitInFlight.get(cacheKey);
    if (existingPromise) {
      return (await existingPromise) as AILimitStatusResponse;
    }

    const requestPromise = axios
      .get(`${BASE_URL}/api/v1/sharetypes/ai-limit-status`, { headers })
      .then((response) => {
        aiLimitCache.set(cacheKey, { response: response.data, fetchedAt: Date.now() });
        return response.data as AILimitStatusResponse;
      })
      .catch((error) => {
        lastAiLimitErrorTime = Date.now();
        throw error;
      })
      .finally(() => {
        aiLimitInFlight.delete(cacheKey);
      });

    aiLimitInFlight.set(cacheKey, requestPromise);
    return (await requestPromise) as AILimitStatusResponse;
  } catch (error: unknown) {
    logger.error('Get AI limit status error:', error);
    const errorMessage =
      error instanceof AxiosError && error.response?.data?.message
        ? error.response.data.message
        : i18n.t('errors.getAiLimitStatusFailed') || 'Failed to get AI limit status';
    throw new Error(errorMessage);
  }
};
